import { z } from "zod";
import { HerSkinSessionIdSchema, IsoDateTimeSchema, StableIdSchema } from "./common.js";

export const FileSourceSchema = z.enum(["personal", "cron"]);
export const FilePresentationSchema = z.enum([
  "image",
  "video",
  "text",
  "markdown",
  "download_only",
]);

const ControlledFileUrlSchema = z
  .string()
  .max(512)
  .regex(/^\/api\//u, "文件 URL 必须是 HerSkin 同源受控接口");

/** 浏览器只接收逻辑文件资源；任何宿主绝对路径都禁止进入这个契约。 */
export const FileResourceSchema = z
  .object({
    fileId: StableIdSchema,
    name: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(160),
    sizeBytes: z
      .number()
      .int()
      .min(0)
      .max(1024 * 1024 * 1024),
    source: FileSourceSchema,
    sourceLabel: z.string().min(1).max(180),
    presentation: FilePresentationSchema,
    available: z.boolean(),
    contentUrl: ControlledFileUrlSchema.nullable(),
    downloadUrl: ControlledFileUrlSchema.nullable(),
    sessionId: HerSkinSessionIdSchema.nullable(),
    cronJobId: StableIdSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine((file, context) => {
    if (!file.available && (file.contentUrl || file.downloadUrl)) {
      context.addIssue({
        code: "custom",
        path: ["available"],
        message: "不可用文件不能提供内容或下载 URL",
      });
    }
    if (file.source === "personal" && !file.sessionId) {
      context.addIssue({
        code: "custom",
        path: ["sessionId"],
        message: "会话附件必须关联 HerSkin Session",
      });
    }
    if (file.source === "cron" && !file.cronJobId) {
      context.addIssue({
        code: "custom",
        path: ["cronJobId"],
        message: "Cron 产物必须关联 CronJob",
      });
    }
  });

export const FileCatalogSchema = z
  .object({
    items: z.array(FileResourceSchema).max(2_000),
    counts: z
      .object({
        all: z.number().int().min(0),
        personal: z.number().int().min(0),
        cron: z.number().int().min(0),
      })
      .strict(),
    cronSourceAvailable: z.boolean(),
    truncated: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

/** Controller/root-helper 之间的固定 Hermes Cron output 投影。 */
export const CronArtifactItemSchema = z
  .object({
    fileId: StableIdSchema.regex(/^file_cron_[a-f0-9]{32}$/u),
    filename: z.string().regex(/^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}\.md$/u),
    mimeType: z.literal("text/markdown"),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(20 * 1024 * 1024),
    cronJobId: z.string().regex(/^[a-f0-9]{12}$/u),
    updatedAt: IsoDateTimeSchema,
    available: z.boolean(),
  })
  .strict();

export const CronArtifactCatalogSchema = z
  .object({
    items: z.array(CronArtifactItemSchema).max(1_000),
    total: z.number().int().min(0),
    truncated: z.boolean(),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const CronArtifactContentSchema = z
  .object({
    fileId: StableIdSchema.regex(/^file_cron_[a-f0-9]{32}$/u),
    filename: z.string().regex(/^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}\.md$/u),
    mimeType: z.literal("text/markdown"),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(512 * 1024),
    updatedAt: IsoDateTimeSchema,
    content: z.string().max(512 * 1024),
  })
  .strict();

export type FileSource = z.infer<typeof FileSourceSchema>;
export type FilePresentation = z.infer<typeof FilePresentationSchema>;
export type FileResource = z.infer<typeof FileResourceSchema>;
export type FileCatalog = z.infer<typeof FileCatalogSchema>;
export type CronArtifactItem = z.infer<typeof CronArtifactItemSchema>;
export type CronArtifactCatalog = z.infer<typeof CronArtifactCatalogSchema>;
export type CronArtifactContent = z.infer<typeof CronArtifactContentSchema>;
